//  Dependencies.
import React from 'react';
import _ from 'lodash';

/*
   used to determine whether or not collection is returned from BE
   in asc order, this would be true if the collection's customOrderStrategy
   is set to bottom. Otherwise the sort order is always 'desc'
*/
const isCollectionAscOrder = (minScore, maxScore) => {
  return minScore < maxScore;
};

/*
    Returns the appropriate column width selector for the collection to be
    used for injecting into Masonry init options.
*/
const getMasonryColWidthSelector = items => {
  // Loop over display items and build combined list of display strategies.
  const displayStrategies = React.Children.map(items, item =>
    _.get(item.props, 'item.strategy.displayStrategy').toLowerCase()
  );

  // Define selector.
  // We always want to use the smallest possible column width so the logic is:
  // - if 'show' strategy exists, use that (smallest size item)
  // - otherwise, if 'feature' strategy exists use that (medium size item)
  // - otherwise, if 'spotlight' strategy exists use that (largest size item)
  // - otherwise, if none of the above exist, just use the first item we encounter
  let selector;
  if (_.includes(displayStrategies, 'show')) {
    selector = '.xItem.xDisplayShow';
  } else if (_.includes(displayStrategies, 'feature')) {
    selector = '.xItem.xDisplayFeature';
  } else if (_.includes(displayStrategies, 'spotlight')) {
    selector = '.xItem.xDisplaySpotlight';
  } else {
    selector = '.xItem';
  }

  return selector;
};

const separatePinnedContent = items => {
  return _.partition(items, item => !!_.get(item, 'meta.pinned'));
};

// - - - - - - - - - - - - - - - - - - - - - - - - - - - - - -
/**
 * resolveItems
 * used to resolve ordering of all items when fetching previous items (auto-polling)
 *
 * @param nonPinnedItems: {array} list of items with no "pinned" value
 * @param pinnedItems: {array} list of items with a "pinned" value (1-10)
 * @return {array} list of items where pinned values are respected, items with same pinned values are grouped
 *
 * return ex: [{ pinned: 1 }, { pinned: 1 }, { pinned: null }, { pinned: 4 }, { pinned: 4 }, { pinned: 5 }]
 */
const resolveItems = (nonPinnedItems, pinnedItems) => {
  // start list off with non-pinned content
  let updatedItems = nonPinnedItems;

  // inject items that have pinned values into current list of non-pinned items.
  _.forEach(pinnedItems, (item, idx) => {
    const pinValueIdx = item.meta.pinned - 1;

    // check if there is a dup pin value as items are added to list
    const itemWithPinValue = _.find(updatedItems, currentItem => currentItem.meta.pinned === item.meta.pinned);

    // check if there is already something pinned at pinValueIdx
    const pinnedItemAtPinValue =
      updatedItems[pinValueIdx] && !!updatedItems[pinValueIdx].meta.pinned ? updatedItems[pinValueIdx] : undefined;

    if (itemWithPinValue) {
      // splice in after last item with same pinned value (group items of same pin value while maintaining desc order)
      const pinnedValue = item.meta.pinned;
      updatedItems = injectPinnedItemAtOffset(pinnedValue, updatedItems, item);
    } else if (pinnedItemAtPinValue) {
      // bump item out of pinned value position because there are previous pinned content items taking the position
      const pinnedValue = pinnedItemAtPinValue.meta.pinned;
      updatedItems = injectPinnedItemAtOffset(pinnedValue, updatedItems, item);
    } else {
      // just assign to exact position
      updatedItems.splice(pinValueIdx, 0, item);
    }
  });
  return updatedItems;
};

const injectPinnedItemAtOffset = (pinnedValue, items, item) => {
  const lastItemWithPinnedValueIdx = _.findLastIndex(items, item => item.meta.pinned === pinnedValue);
  items.splice(lastItemWithPinnedValueIdx + 1, 0, item);
  return items;
};

export {
  getMasonryColWidthSelector,
  isCollectionAscOrder,
  separatePinnedContent,
  resolveItems,
  injectPinnedItemAtOffset,
};
